/**
 * CAOS-1734 — one instant, rendered for the viewer looking at it.
 *
 * THIS IS A MIRROR, AND THAT IS THE DESIGN RATHER THAN AN OVERSIGHT. `formatInstantDisplay` in
 * `kernel/src/api/record-serialization.ts` says the same thing in the same words, and it is what a
 * record page's display block renders. There are two implementations because the roster renders in
 * the BROWSER and the kernel is a Worker: `caos-ui/src/components/field-renderer.ts` imports from
 * `@caos/kernel` type-only on purpose, and a value import would pull the kernel runtime into a
 * browser bundle.
 *
 * Two implementations of one rendering drift unless something holds them together, so something
 * does: `kernel/test/fixtures/instant-display-cases.ts` is the table both sides are asserted
 * against, and `host/test/caos-1734-a-roster-column-can-say-when.test.ts` keeps a mirror of it that
 * names the original. That is the arrangement CAOS-1674 settled for the two address formatters and
 * CAOS-642 for the two place formatters, and this is the third of the same shape.
 *
 * ## Who decides what
 *
 * The AUTHOR decides the precision, in metadata, on the column. The VIEWER decides everything else.
 * Passing `undefined` for the locale is what makes `Intl` read the browser's own locale, and
 * omitting `timeZone` is what makes it read the browser's own zone — so "the viewer" here is the
 * one the browser already holds rather than a second notion of it invented on the roster.
 */

/** The precisions a column may ask for. Mirrors the kernel's `InstantPrecision`. */
export type InstantPrecision = 'date' | 'dateTime' | 'time';

/*
 * THREE SHAPES ARRIVE AND THEY ARE NOT RENDERED ALIKE, which is why the parse reports which one it
 * saw rather than only the instant it produced.
 *
 *   `day`       a bare `2026-10-16` — no clock and no zone
 *   `zoneless`  a datetime with no offset — a clock, but no zone
 *   `instant`   a datetime carrying `Z` or an offset — a real point in time
 *
 * ONLY `instant` MAY BE MOVED INTO THE VIEWER'S ZONE. The other two name no instant, so shifting
 * them does not translate a time, it invents one.
 */
type InstantShape = 'day' | 'zoneless' | 'instant';

/** `2026-10-16` — a stored calendar day, which is what `dateToWire` produces for a `Date` column. */
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;
/*
 * A full ISO-8601 instant: date, clock, and optionally seconds, a fraction and an offset.
 *
 * THE OFFSET GROUP ACCEPTS A TWO-DIGIT OFFSET, and that is not pedantry. `+00` with no minutes is
 * the form Postgres prints for a `timestamptz`, and a narrower group turned a value that used to
 * render into a raw string — a NARROWING dressed up as a tightening. The read path happens to be
 * safe because `instantToWire` normalises through `toISOString()`, but a roster reads a route's raw
 * JSON, so "no route emits that today" is not a property of this function.
 *
 * V8 PARSES `+00` ONLY ON THE SPACE-SEPARATED FORM, which is the one Postgres actually prints:
 * `2026-10-16 18:46:22+00` parses and `2026-10-16T18:46:22+00` is NaN. The grammar admits both and
 * the NaN guard below refuses the second, so it falls back to the raw value rather than rendering a
 * wrong one. Accepting the group is still right, because the Postgres shape is the one that
 * arrives — but an earlier version of this comment said flatly that V8 parses it, which was true of
 * only one of the two separators.
 *
 * Lowercase `t` and `z` are accepted for a related reason: ISO-8601 permits them, V8 parses them,
 * and refusing them would only make a legitimate value disappear.
 *
 * THE OFFSET IS OPTIONAL AND THAT IS LOAD-BEARING: group 3 being undefined is how `parseInstant`
 * tells a zone-carrying instant from a bare clock, so the shape question is answered by THIS
 * grammar rather than by a second pattern that could drift out of step with it.
 */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}[Tt ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?([Zz]|[+-]\d{2}(:?\d{2})?)?$/;

/*
 * Parse only the shapes the platform actually stores, and refuse the rest.
 *
 * `new Date(string)` on its own is far too permissive to sit behind a fall-back-to-raw contract.
 * V8 reads "5" as 1 May 2001, "2026" as 31 Dec 2025 and "12345" as 1 Jan 12345 — so a `format` on
 * a path that happens to hold a short code rendered a PLAUSIBLE WRONG DATE where the contract says
 * it should fall back to what was stored. A wrong date is worse than a raw value precisely because
 * nothing about it looks wrong.
 *
 * `shape` comes back as well as the instant because the three shapes are rendered differently and
 * only the parse knows which one it saw.
 */
/*
 * Is the DATE WRITTEN at the front of this value a day that actually exists?
 *
 * `Number.isNaN` does not answer that. `2026-13-45` is NaN and refused, but `2026-02-30` is NOT:
 * V8 ROLLS IT FORWARD to 2 March, so a date that does not exist renders as though it did — the
 * same class of wrongness as a shifted zone, one calendar further in. `2026-02-29` and
 * `2026-04-31` go the same way.
 *
 * WHAT IS VALIDATED IS THE WRITTEN PREFIX, BEFORE ANY OFFSET IS APPLIED, and that is what lets one
 * check guard both shapes. Comparing the PARSED UTC day against the written one works for a bare
 * calendar day and is wrong for an instant carrying an offset, where the UTC day may legitimately
 * differ from the day written — `2026-10-16T23:00:00-05:00` really is the 17th in UTC. Validating
 * the prefix on its own is offset-independent, so it rejects the impossible without rejecting the
 * merely time-shifted.
 *
 * Returning false for a value with no date prefix at all is deliberate: nothing this function is
 * asked about is an instant without one.
 */
function writtenDayExists(text: string): boolean {
  const written = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (written === null) return false;
  const [year, month, day] = [Number(written[1]), Number(written[2]), Number(written[3])];
  /*
   * `setUTCFullYear` AND NOT `Date.UTC`, WHICH HAS A LEGACY TWO-DIGIT-YEAR MAPPING.
   *
   * `Date.UTC(99, 1, 28)` is 1999, not the year 99, so the year read-back disagreed with the input
   * and this function refused `0099-02-28` — a day that exists. That is the way this check fails:
   * not by letting a bad date through, but by REFUSING A GOOD ONE, and the two positive controls in
   * the case table (a leap day, an offset crossing midnight) did not cover it. `setUTCFullYear`
   * carries no such mapping, and `new Date(0)` is only a carrier for it.
   *
   * It matters for the leap rule too, not just the label: year 0 IS a leap year under the proleptic
   * Gregorian calendar and 1900 is not, so the mapped year would have refused `0000-02-29` as well.
   */
  const utc = new Date(0);
  utc.setUTCFullYear(year, month - 1, day);
  return utc.getUTCFullYear() === year && utc.getUTCMonth() === month - 1 && utc.getUTCDate() === day;
}

function parseInstant(value: string): { readonly when: Date; readonly shape: InstantShape } | null {
  const text = value.trim();
  if (!writtenDayExists(text)) return null;
  if (CALENDAR_DAY.test(text)) {
    // No NaN check here, and that is not an omission. CALENDAR_DAY has matched the WHOLE string and
    // writtenDayExists has already established the day, so this cannot be NaN or rolled over. A
    // guard that cannot fire reads as a safety and is not one.
    return { when: new Date(`${text}T00:00:00Z`), shape: 'day' };
  }
  const shaped = ISO_INSTANT.exec(text);
  if (shaped === null) return null;
  /*
   * A DATETIME WITH NO OFFSET NAMES A CLOCK, NOT AN INSTANT, and `new Date` reads one in the
   * AMBIENT zone — UTC in the Worker, the viewer's own in the browser. So the two copies of this
   * formatter rendered ONE STORED VALUE AS TWO DIFFERENT CLOCKS, five hours apart for a US viewer,
   * and across a DST gap as two different DAYS: `2026-03-08T02:30:00` read as 7 March in the
   * kernel and 8 March in the host. Byte-identical source could not prevent it, because the
   * divergence is in the ambient zone and not in the code.
   *
   * Reading it as UTC and rendering it in UTC shows the clock that was written, identically on both
   * sides. Postgres prints exactly this shape for a `timestamp` without a zone, and a roster reads
   * a route's raw JSON, so this is a shape that arrives rather than one invented for a test.
   *
   * The separator is normalised to `T` before the `Z` goes on. THAT LINE IS NOT PINNED BY ANY TEST
   * AND THIS SAYS SO RATHER THAN IMPLYING OTHERWISE: V8 parses `2026-10-16 18:46:22Z` with the
   * space intact, so dropping the normalisation kills no case. It is there for the OTHER engines
   * one copy of this formatter runs in: the host mirror renders in the browser, and a
   * space-separated ISO string is a form some engines have historically refused. Both copies carry
   * the line so the two stay byte-identical. The test runtime is V8, so nothing in this repo can
   * tell the difference; that is a limit of the suite, not evidence the line is dead.
   *
   * The zoned branch deliberately keeps `new Date(text)` with the space intact, because normalising
   * it would turn `2026-10-16 18:46:22+00` — the `timestamptz` form Postgres actually prints, which
   * a case in the shared table pins — into NaN.
   */
  const zoned = shaped[3] !== undefined;
  // The NaN guard CAN fire, which is why it stays: the clock is matched as `\d{2}:\d{2}`, so
  // `2026-10-16T25:00:00Z` is shaped like an instant, has a real date prefix, and is still NaN.
  const when = new Date(zoned ? text : `${text.replace(' ', 'T')}Z`);
  return Number.isNaN(when.getTime()) ? null : { when, shape: zoned ? 'instant' : 'zoneless' };
}

/**
 * Render `value` as a date a person reads, or return `null` when it is not an instant.
 *
 * `null` rather than a thrown error or the string "Invalid Date", for the same reason the kernel's
 * copy answers `null`: a cell is a render path, and a value this cannot parse has to leave the
 * caller free to show what it actually has.
 *
 * `at` exists for the parity test and for nothing else in production: both halves default to
 * undefined, which is the viewer's own. It is a parameter rather than a module-level override
 * because a test that mutates shared state to pin a locale leaks into every test after it.
 *
 * THE DEFAULT LOCALE IS NOT OBSERVABLE THROUGH THE OUTPUT ON THIS DEVELOPMENT MACHINE, which is a
 * much smaller claim than the one that stood here before and is the reason the test is a spy.
 * Node resolves `Intl`'s default to `en-US` on Windows and did not re-read it from `LANG` or
 * `LC_ALL` — measured there, and NOT measured on the Linux runner CI uses, where ICU ordinarily
 * does read them. So on this machine "fall back to the viewer's locale" and "fall back to en-US"
 * render identically, and a change from the first to the second is invisible in the result while
 * being a real defect in a browser, whose ambient locale is whatever the viewer set.
 *
 * An earlier version of this comment concluded from that that the behaviour could not be pinned at
 * all. It can: the ARGUMENT reaching `Intl.DateTimeFormat` is observable whatever the ambient
 * locale is, on any platform, and the CAOS-1734 host suite asserts it is `undefined` here and the
 * caller's value when one is given. The default ZONE never had the problem — it follows `TZ`, and
 * both suites set it and assert the default path honours it.
 *
 * A `Date` IS ALWAYS AN INSTANT, and that is a contract rather than an accident. A caller holding a
 * stored calendar DAY must pass the wire string and not a `Date`: `new Date('2026-10-16')` is UTC
 * midnight, so rendering it in the viewer's zone is a day early everywhere west of UTC — the exact
 * error this function exists to prevent, reintroduced through the one argument shape that skips the
 * parse. Nothing reachable today passes such a `Date`, and the `Date` case in
 * `host/test/caos-1734-a-roster-column-can-say-when.test.ts` is what keeps that true.
 */
export function formatInstant(
  value: unknown,
  precision: InstantPrecision,
  at: { readonly locale?: string; readonly timeZone?: string } = {},
): string | null {
  if (typeof value !== 'string' && !(value instanceof Date)) return null;
  const parsed =
    value instanceof Date ? { when: value, shape: 'instant' as const } : parseInstant(value);
  if (parsed === null) return null;
  // A BARE CALENDAR DAY HAS NO CLOCK, so a clock precision falls back to the raw value rather than
  // rendering an invented midnight. `format` is authorable on any column and cannot tell a `Date`
  // column from a `DateTime` one, and showing `2026-10-16` as "12:00 AM" states a time the record
  // does not hold — the same fabrication as a shifted day, one unit further in.
  if (parsed.shape === 'day' && precision !== 'date') return null;
  const options: Intl.DateTimeFormatOptions = {
    ...(precision === 'time' ? {} : { dateStyle: 'medium' }),
    ...(precision === 'date' ? {} : { timeStyle: 'short' }),
    // A VALUE THAT NAMES NO INSTANT IS NOT MOVED INTO A ZONE, so the viewer's is deliberately
    // ignored for both such shapes rather than merged with them.
    // `new Date('2026-10-16')` is UTC midnight, and rendering that anywhere west of UTC gives the
    // day BEFORE — which is how "16 Oct" becomes "15 Oct" for half the world. CAOS-1468 fixed the
    // same whole-day error on the WRITE side in `dateToWire`; this is the read side of it. An
    // offset-less datetime is the same error with a clock attached, so it is read as UTC above and
    // rendered in UTC here: what was written is what appears, in the Worker and the browser alike.
    ...(parsed.shape !== 'instant' ? { timeZone: 'UTC' } : at.timeZone ? { timeZone: at.timeZone } : {}),
  };
  return new Intl.DateTimeFormat(at.locale, options).format(parsed.when);
}
